import { ADD_ON_IDS, FE_ADD_ON_ID, PremiumPlanIds, RatingCallRequest } from '@ctct/domain-layer';
import type {
  AddOnsVO,
  BillingAccount,
  Entitlement,
  Rating,
  RatingRequestCustomer,
  RatingResponse,
} from '@ctct/domain-layer';
import type { RatingPayload } from '@ctct/infrastructure-layer';
import type { SelectedAddOn } from '@ctct/state-client-layer';

import { orderSummaryEventBus } from '../OrderSummaryEventBus';
import type { OrderSummaryPageStore } from '../types';

export class RatingCallService {
  private readonly CONTACTS_USAGE_TYPE = 'CONTACTS';

  private readonly SMS_USAGE_TYPE = 'SMS';

  constructor(
    private pageStore: OrderSummaryPageStore,
    private isStripeEnabled: boolean,
    private addOnsQuery?: AddOnsVO,
    private rating?: Rating,
    private SMSEntitlementQuery?: Entitlement,
    billingAccountQuery?: BillingAccount
  ) {
    this.setStripeEnabled(isStripeEnabled);

    if (rating) {
      this.emitEventsWithUpdatedRating(rating);
    }

    if (billingAccountQuery) {
      this.updateCurrency(billingAccountQuery);
    }

    if (addOnsQuery) {
      this.addExistingAddOns(addOnsQuery);
    }
  }

  public api() {
    return {
      getRatingResponse: (): RatingResponse | null => this.rating?.getApiResponse() ?? null,

      getStripeQuoteId: () => this.rating?.getStripeQuoteId() ?? null,

      getRating: () => this.rating,

      initRatingPayload: (
        ratingPayloadJson: Omit<RatingPayload, 'includeTax' | 'usageData' | 'existingAddOnServices'>,
        contactsTier: string,
        smsAmount?: string
      ) => {
        const usageData = this.getUsageData(contactsTier, smsAmount, ratingPayloadJson.newAddOnServices);
        const existingAddOnServices = this.addOnsQuery?.getExistingAddOnsIds() || [];

        let ratingRequest = RatingCallRequest.fromJson({
          ...ratingPayloadJson,
          includeTax: false,
          usageData,
          existingAddOnServices,
        });

        // for stripe accounts we want to make sure that includeTax is set to true, otherwise rating call will return 500 error
        const shouldIncludeTax = this.shouldIncludeTax(ratingRequest.customer) || this.isStripeEnabled;

        // Set stripeAccount in the same write so the first rating query never goes out without it.
        ratingRequest = ratingRequest.withIncludeTax(shouldIncludeTax).withStripeAccount(this.isStripeEnabled);

        this.pageStore.setState({
          ratingPayload: ratingRequest,
        });
      },

      getRatingPayload: () => this.pageStore.getState().ratingPayload,

      updatePlanGroupId: (planGroupId: number) => {
        const { ratingPayload } = this.pageStore.getState();

        if (ratingPayload) {
          this.pageStore.setState({ ratingPayload: ratingPayload.withPlanGroupId(planGroupId) });
        }
      },

      getTaxData: () => {
        const { ratingPayload } = this.pageStore.getState();

        return ratingPayload?.customer?.getTaxData();
      },

      updateCustomerPartially: (customer: Partial<RatingPayload['customer']>) => {
        const { ratingPayload } = this.pageStore.getState();

        if (ratingPayload) {
          const newCustomer = ratingPayload.customer.withPartialUpdate(customer);

          this.pageStore.setState({
            ratingPayload: ratingPayload.withCustomer(newCustomer).withIncludeTax(this.shouldIncludeTax(newCustomer)),
          });
        }
      },

      getTaxUpdateRequested: () => {
        return this.pageStore.getState().taxUpdateRequested;
      },

      setTaxUpdateRequested: (taxUpdateRequested: boolean) => {
        this.pageStore.setState({ taxUpdateRequested });
      },

      isDowngradingFromPremium: () => {
        return this.isDowngradingFromPremium();
      },
    };
  }

  /**
   * Updates the email plan id in the rating payload.
   *
   * @param newPlanId - The new email plan id.
   */
  public updateEmailPlanId = (newPlanId: number) => {
    const { ratingPayload } = this.pageStore.getState();

    if (ratingPayload) {
      this.pageStore.setState({ ratingPayload: ratingPayload.withNewPlanId(newPlanId) });
    }
  };

  /**
   * Updates the contacts tier in the rating payload.
   *
   * @param tier - The contacts tier.
   */
  public updateContactsTier = (tier: string) => {
    const { ratingPayload } = this.pageStore.getState();

    if (ratingPayload) {
      const newUsageData = ratingPayload.usageData.map((data) =>
        data.usageType === this.CONTACTS_USAGE_TYPE ? this.createUsageData(this.CONTACTS_USAGE_TYPE, tier) : data
      );

      this.pageStore.setState({ ratingPayload: ratingPayload.withUsageData(newUsageData) });
    }
  };

  /**
   * Updates the payment period in the rating payload.
   *
   * @param paymentPeriod - The payment period.
   */
  public updatePaymentPeriod = (paymentPeriod: string) => {
    const { ratingPayload } = this.pageStore.getState();

    if (ratingPayload) {
      this.pageStore.setState({ ratingPayload: ratingPayload.withPaymentPeriod(parseInt(paymentPeriod, 10)) });
    }
  };

  /**
   * Updates the add-ons in the rating payload.
   *
   * @param selectedAddOns - The selected add-ons.
   */
  public updateAddOns = (selectedAddOns: SelectedAddOn[]) => {
    const { ratingPayload } = this.pageStore.getState();

    if (!ratingPayload) {
      return;
    }

    const { usageData } = ratingPayload;
    const filteredAddOns = selectedAddOns.filter((addOn) => addOn.id !== FE_ADD_ON_ID.XERO);
    // sort to ensure the same order of add-ons in the API call, helps caching
    const sortedAddOnIds = filteredAddOns.map((addOn) => addOn.id).sort((a, b) => a.localeCompare(b));

    const hasSms = selectedAddOns.some((addOn) => addOn.id === ADD_ON_IDS.SMS);
    const smsUsage = selectedAddOns.find((addOn) => addOn.id === ADD_ON_IDS.SMS)?.option;
    const usageDataWithoutSms = usageData.filter((u) => u.usageType !== this.SMS_USAGE_TYPE);

    this.pageStore.setState({
      ratingPayload: ratingPayload
        .withNewAddOnServices(sortedAddOnIds)
        .withUsageData(
          hasSms && smsUsage
            ? [...usageDataWithoutSms, this.createUsageData(this.SMS_USAGE_TYPE, smsUsage)]
            : usageDataWithoutSms
        ),
    });
  };

  /**
   * Updates the payment plan id in the rating payload.
   *
   * @param paymentPlanId - The payment plan id.
   */
  public updatePaymentPlanId = (paymentPlanId: number) => {
    const { ratingPayload } = this.pageStore.getState();

    if (ratingPayload) {
      this.pageStore.setState({ ratingPayload: ratingPayload.withPaymentPlanId(paymentPlanId) });
    }
  };

  public isDowngradingFromPremium() {
    const { ratingPayload } = this.pageStore.getState();

    if (!ratingPayload) {
      return false;
    }

    return PremiumPlanIds.includes(ratingPayload.currentPlanId) && !PremiumPlanIds.includes(ratingPayload.newPlanId);
  }

  /**
   * Determines whether tax should be included based on customer address details.
   *
   * @returns A boolean indicating whether to include tax.
   */
  private shouldIncludeTax(customer: RatingRequestCustomer): boolean {
    const { city, country, address1, zipcode, state } = customer.getTaxData();

    // Check if the necessary address fields are provided
    const hasRequiredAddressFields = Boolean(city?.length && country?.length && address1?.length && zipcode?.length);

    // Check if the state is required based on the country
    const isStateRequired = country === 'MX' || country === 'CA' || country === 'US';

    // Include tax if required fields are present and state is provided if required
    return hasRequiredAddressFields && (isStateRequired ? state?.length > 0 : true);
  }

  /**
   * Generates the full usage data array based on conditions.
   *
   * @param contactsTier - The contacts tier usage value.
   * @param smsAmount - The SMS usage value (if applicable).
   * @param addons - List of new add-on services.
   * @returns An array of usage data objects.
   */
  private getUsageData(contactsTier: string, smsAmount: string | undefined, addons: string[]) {
    // Initial usage data with CONTACTS usage
    const usageData = [this.createUsageData(this.CONTACTS_USAGE_TYPE, contactsTier)];

    // Add SMS usage data if the conditions are met
    if (typeof smsAmount === 'string' && addons.includes(ADD_ON_IDS.SMS)) {
      usageData.push(this.createUsageData(this.SMS_USAGE_TYPE, smsAmount));
    }

    return usageData;
  }

  /**
   * Generates usage data for a given usage type.
   *
   * @param usageType - The type of usage (e.g., CONTACTS, SMS).
   * @param used - The usage value as a string (will be sanitized).
   * @returns An object representing usage data.
   */
  private createUsageData(usageType: string, used: string, resetValue = 0, minValue: number | null = null) {
    return {
      usageType,
      used: used.replace(/,/g, ''), // Remove commas from the usage string
      resetValue,
      minValue,
    };
  }

  private addExistingAddOns(addOnsQuery: AddOnsVO) {
    const { ratingPayload } = this.pageStore.getState();

    const willUnsubscribeSmsAfterPurchase = this.isDowngradingFromPremium() && this.SMSEntitlementQuery?.getIsNotUsed();

    if (ratingPayload) {
      // if we are downgrading from premium and SMS is not used, we do not add it to existing add-ons
      // so will filter it out to make sure that the rating payload does not include it
      // SMS addon will be unsubscribed after purchase (addtional call to billing/customers/canceladdonsubscription)
      const existingAddOnsIds = willUnsubscribeSmsAfterPurchase
        ? addOnsQuery.getExistingAddOnsIds().filter((id) => id !== ADD_ON_IDS.SMS)
        : addOnsQuery.getExistingAddOnsIds();

      this.pageStore.setState({
        ratingPayload: ratingPayload.withExistingAddOnServices(existingAddOnsIds),
      });
    }
  }

  private updateCurrency(billingAccountQuery: BillingAccount) {
    const { ratingPayload } = this.pageStore.getState();
    const prevCurrency = ratingPayload?.currencyCode;
    const billingCurrency = billingAccountQuery.getCurrency();

    if (ratingPayload && prevCurrency !== billingCurrency) {
      this.pageStore.setState({
        ratingPayload: ratingPayload.withCurrencyCode(billingCurrency),
      });
    }
  }

  private emitEventsWithUpdatedRating(rating: Rating) {
    orderSummaryEventBus.emit('totalPriceUpdated', rating.getAmountToCharge().format(rating.getCurrency()), {
      source: 'asyncResponse',
    });

    orderSummaryEventBus.emit(
      'paymentDataUpdated',
      {
        amount: rating.getAmountToCharge().priceAmount,
        taxAmount: rating.getTotalTax().priceAmount,
        currency: rating.getCurrency(),
      },
      { source: 'asyncResponse' }
    );

    orderSummaryEventBus.emit('balanceUpdated', rating.getStartingBalance(), {
      source: 'asyncResponse',
    });

    orderSummaryEventBus.emit(
      'planBaseMonthlyPriceUpdated',
      { value: rating.getPlanBaseMonthly(), currency: rating.getCurrency() },
      { source: 'asyncResponse' }
    );
  }

  private setStripeEnabled(isStripeEnabled: boolean) {
    const { ratingPayload } = this.pageStore.getState();
    const wasStripeEnabled = ratingPayload?.stripeAccount === true;

    if (!ratingPayload || wasStripeEnabled === isStripeEnabled) {
      return;
    }

    this.pageStore.setState({
      ratingPayload: ratingPayload.withStripeAccount(isStripeEnabled),
    });
  }
}
